import { EditableText, defineBlock } from "eddev/blocks"
import { cn } from "@utils/tw"
import { Col, Container, Grid } from "@components/layout/Grid"
import { ComponentPropsWithoutRef, forwardRef, useState } from "react"
import { Typography } from "@components/atoms/Typography"
import { VSpace } from "@components/layout/Spacer"
import { Button } from "@components/atoms/Button"
import { CARET_RIGHT } from "@components/atoms/Icons"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Link } from "eddev/routing"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"

export const meta: BlockMeta = {
  title: "Multi Section Widget",
  category: "common",
  icon: "welcome-widgets-menus",
  keywords: ["multi"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/multi-section-widget", (props) => {
  const [active, setActive] = useState<number>(0)
  const isMobile = useIsMobile()
  return (
    <WidgetContainer>
      <Grid>
        <>
          {!isMobile && <Col initial={12} tablet={1} laptop={2} />}
          <Col initial={12} tablet={10} laptop={8}>
            <SubtitleContainer>
              <EditableText as="span" store="subtitle" className="!text-center" placeholder="Enter subtitle..." />
              <EditableText as="h4" store="title" className="!text-center" placeholder="Enter title..." />
            </SubtitleContainer>
          </Col>
          {!isMobile && <Col initial={12} tablet={1} laptop={2} />}
        </>
        <Col initial={12}>
          <Widget>
            <Left>
              <div>
                <EditableText store="widget_subtitle" placeholder="Enter widget subtitle..." as="h5" />
                <VSpace initial={4} />
                <ul>
                  {props.sections?.map((section, i) => (
                    <li key={i}>
                      <button onClick={() => setActive(i)} className={active === i ? `active` : ``}>
                        <span>
                          {CARET_RIGHT} {section?.title}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {props.button && (
                <>
                  <VSpace initial={8} />
                  <Button
                    size="medium"
                    color="yellow"
                    href={props.button?.url ?? ""}
                    target={props.button?.target ? "_blank" : undefined}
                    icon={props.button?.target ? "ARROW_UP_RIGHT" : "ARROW_RIGHT"}
                    iconStyle="right"
                  >
                    {props.button?.title}
                  </Button>
                </>
              )}
            </Left>
            <Right>
              {props.sections && (
                <>
                  <div>
                    <Typography
                      as="div"
                      variant="text-m"
                      dangerouslySetInnerHTML={{ __html: props.sections[active]?.content ?? "" }}
                    />
                  </div>

                  {props.sections[active]?.links && (
                    <>
                      <VSpace initial={8} />
                      <ul>
                        {props.sections[active]?.links?.map((link, i) => (
                          <li key={i}>
                            <LinkCard href={link?.link?.url} target={link?.link?.target ? "_blank" : undefined}>
                              {link?.image && (
                                <div>
                                  <ResponsiveImage {...link?.image} aspect={1} />
                                </div>
                              )}

                              <span>
                                {link?.link?.title}
                                <i>{CARET_RIGHT}</i>
                              </span>
                            </LinkCard>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </Right>
          </Widget>
        </Col>
      </Grid>
    </WidgetContainer>
  )
})

const WidgetContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container
      ref={ref}
      className={cn("my-5 [&_div>button.button]:w-full [&_div>button.button]:box-border", className)}
      {...props}
    />
  ),
)
WidgetContainer.displayName = "WidgetContainer"

const SubtitleContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col items-center gap-5 py-6 w-full",
        "[&>span]:type-label-caps [&>span]:text-mid-grey",
        "[&_h3]:type-title-s [&_h3]:my-0 [&_h3]:text-center",
        className,
      )}
      {...props}
    />
  ),
)
SubtitleContainer.displayName = "SubtitleContainer"

const Widget = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-light-grey py-6 px-7 rounded-md flex flex-col gap-9 md:flex-row md:items-start md:justify-between",
      className,
    )}
    {...props}
  />
))
Widget.displayName = "Widget"

// Mirrors the original stitches selector tree 1:1 (descendant/child
// combinators, same order) — see multi-dropdown-widget.tsx's Left for the
// closely related `ul > li > button > span > svg` shape.
const LEFT_BASE = [
  "w-full self-stretch flex flex-col items-start justify-between",
  "[&>div]:w-full [&>div]:flex [&>div]:flex-col",
  "[&>div_h5]:type-label-caps [&>div_h5]:my-0",
  "[&_ul]:m-0 [&_ul]:p-0",
  "[&_ul_li]:list-none [&_ul_li]:p-0",
  "[&_ul_li_button]:appearance-none [&_ul_li_button]:bg-transparent [&_ul_li_button]:flex [&_ul_li_button]:items-center [&_ul_li_button]:type-title-l [&_ul_li_button]:text-mid-grey [&_ul_li_button]:cursor-pointer [&_ul_li_button]:transition-all [&_ul_li_button]:ease-movement [&_ul_li_button]:duration-300 [&_ul_li_button]:overflow-hidden [&_ul_li_button]:text-left",
  "[&_ul_li_button_span]:flex [&_ul_li_button_span]:items-start [&_ul_li_button_span]:-translate-x-[32px] [&_ul_li_button_span]:transition-all [&_ul_li_button_span]:ease-movement [&_ul_li_button_span]:duration-300",
  "[&_ul_li_button_span_svg]:mt-0 [&_ul_li_button_span_svg]:w-[32px] [&_ul_li_button_span_svg]:h-[32px] [&_ul_li_button_span_svg]:min-w-[32px] [&_ul_li_button_span_svg]:min-h-[32px]",
  "[&_ul_li_button.active]:text-black [&_ul_li_button.active_span]:translate-x-0",
  "[&_ul_li_button:hover]:text-dark-grey [&_ul_li_button:hover.active]:text-black",
  "[&_ul_li_button_svg]:w-[32px] [&_ul_li_button_svg]:h-[32px]",
  "md:w-[calc(100%*(2/3))]",
]

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(LEFT_BASE, className)} {...props} />
))
Left.displayName = "Left"

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full bg-white rounded-md p-5 flex flex-col justify-between self-stretch",
      "[&_ul]:m-0 [&_ul]:p-0 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2",
      "[&_ul_li]:list-none [&_ul_li]:p-0",
      "md:w-[calc(100%*(1/3))]",
      className,
    )}
    {...props}
  />
))
Right.displayName = "Right"

const LinkCard = forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<typeof Link>>(
  ({ className, ...props }, ref) => (
    <Link
      ref={ref}
      className={cn(
        "flex justify-start items-center rounded-md border border-solid border-grey p-2 transition-all ease-movement duration-300 text-black no-underline",
        "[&>div]:w-[64px] [&>div]:min-w-[64px] [&>div]:h-[64px] [&>div]:overflow-hidden [&>div]:rounded-[6px] [&>div]:mr-4",
        "[&>span]:flex [&>span]:items-center [&>span]:justify-between [&>span]:type-body-s [&>span]:my-0 [&>span]:font-semibold [&>span]:grow",
        "hover:-translate-y-[2px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)] hover:text-black",
        className,
      )}
      {...props}
    />
  ),
)
LinkCard.displayName = "LinkCard"
